import { Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { Chip } from 'components/chips'
import { PersonDto } from 'dtos'

interface PersonHeroCardProps {
  greenText?: boolean
  person: PersonDto
}

export default function PersonHeroCard({ children, greenText, person }: React.PropsWithChildren<PersonHeroCardProps>) {
  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm={true} container alignItems='center' spacing={0}>
              <Grid item>
                <Typography fontWeight='bold' variant='h5' sx={{ pr: 1 }}>
                  {person.firstName} {person.lastName}
                </Typography>
              </Grid>

              {person.isActive && (
                <Grid item>
                  <Chip variant='green'>Active</Chip>
                </Grid>
              )}

              {!person.isActive && (
                <Grid item>
                  <Chip variant='gray'>Inactive</Chip>
                </Grid>
              )}

              {person.isDriver && (
                <Grid item>
                  <Chip variant='yellow'>Driver</Chip>
                </Grid>
              )}

              {person.isDeclarant && (
                <Grid item>
                  <Chip variant='orange'>Declarant</Chip>
                </Grid>
              )}

              {person.isDeclarationOfOwnershipSigner && (
                <Grid item>
                  <Chip variant='orange'>Declaration of Ownership Signer</Chip>
                </Grid>
              )}

              {person.isEmployee && (
                <Grid item>
                  <Chip variant='blue'>Employee</Chip>
                </Grid>
              )}

              {person.isTitleClerk && (
                <Grid item>
                  <Chip variant='purple'>Title Clerk</Chip>
                </Grid>
              )}

              {person.isUser && (
                <Grid item>
                  <Chip variant='purple'>User</Chip>
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} sm='auto' container alignItems='center' spacing={1}>
              {children}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={3}>
            <LabelField label='Company' value={person.company?.name} to={person.company ? `/companies/${person.company.companyGuid}` : ''} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <LabelField label='Title' value={person.title} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <LabelField label='Email' value={person.email} to={person.email ? `mailto:${person.email}` : ''} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <LabelField label='Mobile Phone' value={person.mobilePhone} to={person.mobilePhone ? `tel:${person.mobilePhone}` : ''} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <LabelField label='Office Phone' value={person.officePhone} to={person.officePhone ? `tel:${person.officePhone}` : ''} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <LabelField label='Notes' value={person.notes} />
          </Grid>

          {greenText && (
            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Person Name:**

Displays the person's name as entered in the Edit Person Dialog.

**Chips:**

Displays one or more chips indicating the person's access type(s). Possible chips include:

- Active
- Inactive
- Driver
- Declarant
- Employee
- Title Clerk
- Access to VTS2

**EDIT Button:**

Clicking the EDIT button opens the Edit Person Dialog.

**Company:**

Displays the company the person is associated with, as selected in the Edit Person Dialog.

**Title:**

Displays the person's title as entered in the Edit Person Dialog.

If no title has been entered, a “-” is displayed.

**Email Address:**

Displays the person's email address as entered in the Edit Person Dialog.

If no email address has been entered, a “-” is displayed.

**Mobile Phone:**

Displays the person's mobile phone number as entered in the Edit Person Dialog.

If no mobile phone number has been entered, a “-” is displayed.

**Office Phone:**

Displays the person's office phone number as entered in the Edit Person Dialog.

If no office phone number has been entered, a “-” is displayed.

**Notes:**

Displays any notes entered in the Edit Person Dialog.

If no notes have been entered, a “-” is displayed.

**Cards:**

Cards are displayed based on the features toggled on for the person.

Cards are arranged in alphabetical order, displayed from left to right. After three columns, the next row begins, following the same left-to-right arrangement.`}
              </GreenText>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  )
}
